/**
 * Configure a tile — ONE panel for everything a tile is.
 *
 *  - TITLE.
 *  - REPORT: which report the tile shows. Pick a custom report, build one on
 *    the dashboard, or start from the linked one. A custom report's question
 *    is never edited here — it belongs to the report and reaches every
 *    dashboard showing it, so it is changed on its own page.
 *  - FILTERS: a linked tile may add its own on top of the report's; a report
 *    built on the dashboard keeps its filters in its report ("Edit report").
 *  - CHART: how the tile draws.
 *  - STYLE: reserved for colours and formatting.
 *
 * The same panel adds a report tile: it opens with the Report section empty.
 *
 * It holds ONE draft (`TileSettings`) and writes nothing until Apply, which
 * lands in the dashboard's own draft like every other tile edit — "Save for
 * everyone" still persists it, and Cancel on the page still throws it away.
 *
 * Two sub-views swap in over the settings: the report BUILDER (wide) and the
 * custom report PICKER. Both hand their answer back to the settings view.
 */
import type { SavedReport } from '@clockwise/clients/report-definitions';
import {
  applyFilterAction,
  type FilterAction,
  FilterBar,
  filterFieldsOf,
  filterTypeOf,
  hasOpenOverlay,
} from '@clockwise/report-kit';
import type {
  DashboardTile,
  FilterNode,
  ReportModel,
  ReportSpec,
  VizKind,
} from '@clockwise/semantic-core';
import {
  canStack,
  describeFilter,
  MAX_TILE_FILTERS,
  vizAvailability,
} from '@clockwise/semantic-core';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  FilePlus2,
  Hash,
  LayoutDashboard,
  Link2,
  ListFilter,
  Lock,
  Pencil,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { reportFilterControl } from '@/components/reports/report-filter-control';
import '@/components/reports/report-kit-theme.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { reportDefinitionsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { BuilderPanel, describedName } from './builder-panel';
import { CustomReportPicker } from './custom-report-picker';
import { SettingsHint as Note, SettingsSection as Section } from './settings-section';
import { VIZ_LABELS } from './tile-editor';
import {
  newReportSettings,
  settingsOf,
  settingsRefusal,
  type TileSettings,
  withBuiltReport,
  withInlineCopy,
  withLinkedReport,
} from './tile-settings';
import { useFilterLabels } from './use-filter-labels';

/** Said wherever a linked report that cannot be read would otherwise show nothing. */
const UNAVAILABLE =
  'This tile’s custom report can’t be opened — it was deleted or is no longer shared with you. Pick another with Change report.';

/** The charts a report tile may be drawn as, in the order they are offered. */
const REPORT_CHARTS: VizKind[] = ['table', 'bar', 'line', 'area', 'pie', 'metric'];

type View = { kind: 'settings' } | { kind: 'builder'; start?: ReportSpec } | { kind: 'pick' };

interface Props {
  open: boolean;
  /** The tile being configured, or `null` to add a new report tile. */
  tile: DashboardTile | null;
  /** The catalog the dashboard already holds; `undefined` while it loads. */
  models?: readonly ReportModel[];
  /** The dashboard's period: what member lookups are asked over. */
  period: DateRangeValue;
  window: { from: string; to: string };
  /** The colours this tile is painted with on the dashboard — see `tilePalette`. */
  palette?: readonly string[];
  /**
   * Apply. `fallbackTitle` names a report built on the dashboard that was left
   * untitled — the report described in words.
   */
  onApply: (settings: TileSettings, fallbackTitle: string) => void;
  onClose: () => void;
}

export function TileSettingsSheet({
  open,
  tile,
  models,
  period,
  window,
  palette,
  onApply,
  onClose,
}: Props) {
  const [initial] = useState(() => (tile ? settingsOf(tile) : newReportSettings()));
  const [settings, setSettings] = useState<TileSettings>(initial);
  const [view, setView] = useState<View>({ kind: 'settings' });
  /** Tile filters a newly picked report could not run — said once, then gone. */
  const [dropped, setDropped] = useState<string[]>([]);
  /** What a discard prompt is about to throw away: the whole panel, or the builder's draft. */
  const [confirming, setConfirming] = useState<'close' | 'back' | null>(null);
  /** The builder has changes not yet handed back with Done. */
  const [builderDirty, setBuilderDirty] = useState(false);

  const { source } = settings;
  const metric = settings.kind === 'metric';
  const isNew = tile === null;

  // A linked tile's report — the same cache entry the dashboard's own tiles
  // read, so opening the panel costs nothing the dashboard has not paid.
  const linkedId = source.kind === 'linked' ? source.reportId : null;
  const linked = useQuery({
    queryKey: queryKeys.reportDefinitions.detail(linkedId ?? 'none'),
    queryFn: () => reportDefinitionsApi.get(linkedId as string).then((r) => r.data),
    enabled: linkedId !== null,
    staleTime: 60_000,
    retry: false,
  });

  const spec: ReportSpec | undefined =
    source.kind === 'inline'
      ? source.spec
      : source.kind === 'linked'
        ? linked.data?.spec
        : undefined;
  const model = spec ? models?.find((m) => m.id === spec.model) : undefined;
  /** The linked report could not be read — deleted, or no longer shared with this viewer. */
  const linkedFailed = source.kind === 'linked' && linked.isError;
  const reportName =
    source.kind === 'linked'
      ? (linked.data?.name ?? (linkedFailed ? 'Custom report unavailable' : 'Custom report'))
      : '';
  const described = source.kind === 'inline' ? describedName(source.spec, model) : '';

  const dirty = JSON.stringify(settings) !== JSON.stringify(initial);
  const refusal = settingsRefusal(settings);

  /**
   * Every settings change goes through here, and clears the "removed filters"
   * notice: it describes the one pick that caused it, and after anything else
   * it would describe a tile that no longer exists.
   */
  const change = (next: TileSettings) => {
    setSettings(next);
    setDropped([]);
  };

  // The builder's draft counts too: it is work the author would lose, even
  // though it has not reached `settings` yet.
  const requestClose = () => {
    if (dirty || (view.kind === 'builder' && builderDirty)) setConfirming('close');
    else onClose();
  };

  const backToSettings = () => {
    setBuilderDirty(false);
    setView({ kind: 'settings' });
  };
  /** Back from the builder, asking first when it holds changes. */
  const leaveBuilder = () => {
    if (builderDirty) setConfirming('back');
    else backToSettings();
  };

  const apply = () => {
    if (settingsRefusal(settings)) return;
    onApply(settings, described);
  };

  const pick = (report: SavedReport) => {
    const next = withLinkedReport(
      settings,
      report,
      models?.find((m) => m.id === report.spec.model),
    );
    setSettings(next.settings);
    setDropped(next.dropped.map((node) => (model ? describeFilter(model, node) : node.id)));
    setView({ kind: 'settings' });
  };

  const wide = view.kind === 'builder';

  /**
   * The tile, by the name it shows — what the author is looking at on the
   * dashboard. Every view names it, so the builder, opened full width over the
   * tiles, still says which one it is changing.
   */
  const tileName =
    settings.title.trim() ||
    (source.kind === 'linked' ? reportName : source.kind === 'inline' ? described : '');

  return (
    <Sheet open={open} onOpenChange={(next) => !next && requestClose()}>
      <SheetContent
        className={`flex w-full flex-col gap-0 p-0 ${wide ? 'sm:max-w-[min(1200px,95vw)]' : 'sm:max-w-2xl'}`}
        // ONE ESCAPE, ONE LAYER: a press meant to close a builder or filter
        // popover must not close the whole panel with it.
        onEscapeKeyDown={(e) => {
          if (hasOpenOverlay()) e.preventDefault();
        }}
      >
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            {view.kind !== 'settings' && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-ml-2 size-8"
                aria-label="Back to the tile’s settings"
                onClick={view.kind === 'builder' ? leaveBuilder : backToSettings}
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
              </Button>
            )}
            <SheetTitle>
              {view.kind === 'builder'
                ? view.start
                  ? 'Edit report'
                  : 'Build a report'
                : view.kind === 'pick'
                  ? 'Pick a custom report'
                  : isNew
                    ? 'Add a report'
                    : tileName
                      ? `Configure “${tileName}”`
                      : 'Configure tile'}
            </SheetTitle>
          </div>
          <SheetDescription className={view.kind === 'settings' ? 'sr-only' : undefined}>
            {view.kind !== 'settings' && (
              <span className="block font-medium text-foreground">
                {isNew ? 'For the new tile' : `For the tile “${tileName || 'Untitled'}”`}
              </span>
            )}
            {view.kind === 'builder'
              ? 'This report lives only on this dashboard. The period comes from the dashboard; the one here only drives the preview.'
              : view.kind === 'pick'
                ? 'The tile stays linked, so edits to the custom report show here.'
                : 'Title, report, filters and chart for this tile.'}
          </SheetDescription>
        </SheetHeader>

        {view.kind === 'builder' &&
          (models ? (
            <BuilderPanel
              metric={metric}
              models={models}
              initialSpec={view.start}
              palette={palette}
              onDirtyChange={setBuilderDirty}
              onDone={(built) => {
                change(withBuiltReport(settings, built));
                backToSettings();
              }}
              onBack={leaveBuilder}
            />
          ) : (
            <p className="px-6 py-4 text-muted-foreground text-sm">Loading…</p>
          ))}

        {view.kind === 'pick' && (
          <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
            {/* Not before the catalog: a pick is checked against the new
                report's subject area, and unchecked is not the same as fine. */}
            {models ? (
              <CustomReportPicker currentId={linkedId ?? undefined} onPick={pick} />
            ) : (
              <p className="text-muted-foreground text-sm">Loading…</p>
            )}
          </div>
        )}

        {view.kind === 'settings' && (
          <>
            <div className="min-h-0 flex-1 divide-y overflow-auto">
              <Section label="Title" htmlFor="tile-title">
                <Input
                  id="tile-title"
                  value={settings.title}
                  maxLength={200}
                  placeholder={
                    source.kind === 'linked'
                      ? reportName
                      : source.kind === 'inline'
                        ? described || 'Untitled report'
                        : 'Untitled report'
                  }
                  onChange={(e) => change({ ...settings, title: e.target.value })}
                />
                <Note>
                  {source.kind === 'linked'
                    ? 'Leave empty to use the report’s name.'
                    : metric
                      ? 'Leave empty to name it after its measure.'
                      : 'Leave empty to name it after what it shows.'}
                </Note>
              </Section>

              <Section label="Report">
                <ReportCard
                  settings={settings}
                  reportName={reportName}
                  described={described}
                  linkedLoaded={!!linked.data}
                  linkedFailed={linkedFailed}
                  onPick={() => setView({ kind: 'pick' })}
                  onBuild={() => setView({ kind: 'builder' })}
                  onEdit={() =>
                    source.kind === 'inline' &&
                    setView({
                      kind: 'builder',
                      start: metric
                        ? source.spec
                        : {
                            ...source.spec,
                            viz: settings.view.viz,
                            stacked: settings.view.stacked,
                          },
                    })
                  }
                  onCopy={() =>
                    linked.data &&
                    change(
                      withInlineCopy(settings, { spec: linked.data.spec, name: linked.data.name }),
                    )
                  }
                />
                {dropped.length > 0 && (
                  <p role="status" className="mt-2 text-amber-700 text-xs dark:text-amber-400">
                    Removed filters the new report can’t use: {dropped.join('; ')}.
                  </p>
                )}
              </Section>

              <Section label="Filters">
                <FiltersSection
                  settings={settings}
                  spec={spec}
                  model={model}
                  period={period}
                  window={window}
                  linkedFailed={linkedFailed}
                  onFilters={(filters) => change({ ...settings, filters })}
                />
              </Section>

              <Section label="Chart">
                {metric ? (
                  <>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" aria-pressed>
                        Number
                      </Button>
                    </div>
                    <Note>A number tile always shows one number with its trend.</Note>
                  </>
                ) : (
                  <ChartPicker
                    spec={spec}
                    model={model}
                    view={settings.view}
                    linkedFailed={linkedFailed}
                    onView={(next) => change({ ...settings, view: next })}
                  />
                )}
              </Section>

              <Section label="Style" muted>
                <div className="flex flex-wrap gap-1.5">
                  {['Colours', 'Number format', 'Legend'].map((label) => (
                    <span
                      key={label}
                      className="rounded-full border bg-muted px-2.5 py-1 text-muted-foreground text-xs"
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <Note>Coming later.</Note>
              </Section>
            </div>

            <SheetFooter className="flex-row items-center justify-end gap-2 border-t px-6 py-3">
              {refusal && <p className="mr-auto text-muted-foreground text-xs">{refusal}</p>}
              <Button type="button" variant="ghost" onClick={requestClose}>
                Cancel
              </Button>
              <Button type="button" onClick={apply} disabled={refusal !== null}>
                {isNew ? 'Add to dashboard' : 'Apply'}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === 'back'
            ? 'Discard your changes to this report?'
            : isNew
              ? 'Discard this report?'
              : 'Discard your changes to this tile?'
        }
        description={
          confirming === 'back'
            ? 'What you changed in the builder hasn’t been used yet. Press Done to keep it.'
            : 'What you changed here hasn’t been applied to the dashboard.'
        }
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          const what = confirming;
          setConfirming(null);
          if (what === 'back') backToSettings();
          else onClose();
        }}
        onCancel={() => setConfirming(null)}
      />
    </Sheet>
  );
}

/** What the tile shows, and the ways to change it. */
function ReportCard({
  settings,
  reportName,
  described,
  linkedLoaded,
  linkedFailed,
  onPick,
  onBuild,
  onEdit,
  onCopy,
}: {
  settings: TileSettings;
  reportName: string;
  described: string;
  linkedLoaded: boolean;
  linkedFailed: boolean;
  onPick: () => void;
  onBuild: () => void;
  onEdit: () => void;
  onCopy: () => void;
}) {
  const { source } = settings;
  const metric = settings.kind === 'metric';

  if (source.kind === 'none') {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <SourceButton
          icon={<Link2 className="size-5 text-muted-foreground" aria-hidden="true" />}
          title="Use a custom report"
          body="Stays linked: edits to the report show here."
          onClick={onPick}
        />
        <SourceButton
          icon={<FilePlus2 className="size-5 text-primary" aria-hidden="true" />}
          title="Build a new report"
          body="Lives only on this dashboard. It isn’t listed in Custom Reports."
          onClick={onBuild}
        />
      </div>
    );
  }

  const linked = source.kind === 'linked';
  const Icon = linked ? Link2 : metric ? Hash : LayoutDashboard;

  return (
    <>
      <div className="flex items-center gap-3 rounded-md border px-3 py-2.5">
        <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-sm">
            {linked ? reportName : settings.title.trim() || described || 'Report'}
          </div>
          <div className="truncate text-muted-foreground text-xs">
            {linked
              ? linkedFailed
                ? 'Deleted, or no longer shared with you — pick another with Change report'
                : 'Custom report · edits to it show here'
              : metric
                ? 'Number built on this dashboard'
                : 'Built on this dashboard · not in Custom Reports'}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {linked ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onPick}>
                <Link2 className="size-3.5" aria-hidden="true" />
                Change report
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCopy}
                disabled={!linkedLoaded}
                title="Build a report on this dashboard, starting from this one. Edits to the custom report won’t reach it any more."
              >
                <Copy className="size-3.5" aria-hidden="true" />
                Copy to dashboard
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="size-3.5" aria-hidden="true" />
                Edit report
              </Button>
              {!metric && (
                <Button type="button" variant="outline" size="sm" onClick={onPick}>
                  <Link2 className="size-3.5" aria-hidden="true" />
                  Use a custom report
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {linked && (
        <Note>
          To change the report itself, open it in{' '}
          <Link
            to="/reports/custom/$reportId"
            params={{ reportId: source.reportId }}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-0.5 underline underline-offset-2"
          >
            Custom Reports
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
          . Changes there reach every dashboard showing it.
        </Note>
      )}
    </>
  );
}

function SourceButton({
  icon,
  title,
  body,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-1.5 rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {icon}
      <span className="font-medium text-sm">{title}</span>
      <span className="text-muted-foreground text-xs leading-relaxed">{body}</span>
    </button>
  );
}

/**
 * The filters, by who owns them.
 *
 * A LINKED tile: the report's own, locked — they are part of what the report
 * is — then the tile's own, editable on the same bar the dashboard uses.
 * A report built on the dashboard: its filters are its report's, so they are
 * read here and changed with "Edit report".
 */
function FiltersSection({
  settings,
  spec,
  model,
  period,
  window,
  linkedFailed,
  onFilters,
}: {
  settings: TileSettings;
  spec?: ReportSpec;
  model?: ReportModel;
  period: DateRangeValue;
  window: { from: string; to: string };
  linkedFailed: boolean;
  onFilters: (filters: FilterNode[]) => void;
}) {
  const own = spec?.filters ?? [];
  const labelOf = useFilterLabels(model, own, window);
  const described = useMemo(
    () => (model ? own.map((node) => describeFilter(model, node, labelOf)) : []),
    [model, own, labelOf],
  );
  const fields = useMemo(() => (model ? filterFieldsOf(model) : []), [model]);
  const typeOf = useMemo(() => filterTypeOf(fields), [fields]);

  if (settings.source.kind === 'none') return <Note>Pick or build a report first.</Note>;
  if (linkedFailed) return <Note>{UNAVAILABLE}</Note>;
  if (!spec || !model) return <Note>Loading…</Note>;

  const linked = settings.source.kind === 'linked';
  const onAction = (action: FilterAction) =>
    onFilters([...applyFilterAction(settings.filters, action, typeOf)]);

  return (
    <>
      {described.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="The report’s own filters">
          {described.map((text) => (
            <li
              key={text}
              className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-muted-foreground text-xs"
              title={linked ? 'From the custom report.' : 'Part of this report.'}
            >
              {linked ? (
                <Lock className="size-3" aria-hidden="true" />
              ) : (
                <ListFilter className="size-3" aria-hidden="true" />
              )}
              {text}
            </li>
          ))}
        </ul>
      ) : (
        !linked && <Note>This report has no filters.</Note>
      )}
      {linked ? (
        <>
          <FilterBar
            fields={fields}
            filters={settings.filters}
            onAction={onAction}
            period={{ value: period, window, onChange: () => {} }}
            showPeriod={false}
            renderControl={reportFilterControl}
            canAdd={settings.filters.length < MAX_TILE_FILTERS}
            label=""
            className="report-kit-tokens flex flex-wrap items-center gap-2"
          />
          <Note>
            Filters you add here narrow this tile only. A dashboard filter on the same field doesn’t
            override them.
          </Note>
        </>
      ) : (
        <Note>Part of the report. Change them with Edit report.</Note>
      )}
    </>
  );
}

/** How the tile draws — only the charts this report can actually be drawn as. */
function ChartPicker({
  spec,
  model,
  view,
  linkedFailed,
  onView,
}: {
  spec?: ReportSpec;
  model?: ReportModel;
  view: TileSettings['view'];
  linkedFailed: boolean;
  onView: (view: TileSettings['view']) => void;
}) {
  if (linkedFailed) return <Note>{UNAVAILABLE}</Note>;
  if (!spec || !model) return <Note>Pick or build a report first.</Note>;
  if (spec.kind === 'detail') return <Note>A listing is always shown as a table.</Note>;
  const stack = canStack(model, { ...spec, viz: view.viz, stacked: true });
  // WHY each chart is or is not available, in the guard's own words — a
  // disabled button with no reason gives the author nothing to act on.
  const availability = vizAvailability(model, spec);
  return (
    <>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Chart">
        {REPORT_CHARTS.map((viz) => {
          const verdict = availability.get(viz);
          const ok = verdict?.ok ?? false;
          return (
            <Button
              key={viz}
              type="button"
              size="sm"
              role="radio"
              aria-checked={view.viz === viz}
              variant={view.viz === viz ? 'default' : 'outline'}
              disabled={!ok}
              title={verdict && !verdict.ok ? verdict.reason : undefined}
              onClick={() => onView({ viz, stacked: view.stacked })}
            >
              {VIZ_LABELS[viz]}
            </Button>
          );
        })}
      </div>
      {stack.ok && (
        <div className="flex items-center gap-2 pt-1">
          <Switch
            id="tile-stacked"
            checked={view.stacked}
            onCheckedChange={(stacked) => onView({ ...view, stacked })}
          />
          <Label htmlFor="tile-stacked" className="font-normal">
            Stacked
          </Label>
        </div>
      )}
    </>
  );
}
